import {
  IApplicationConfigurationBrandingKeyTypeModel,
  IApplicationConfigurationBrandingValueModel,
} from "@xala/common-services";
import {
  Checkbox,
  Choose,
  ChooseOption,
  ColorPicker,
  Input,
  ITableColumnProps,
  Table,
} from "@xala/common-ui";
import { Form } from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation, UseTranslationResponse } from "react-i18next";
import {
  allowedFonts,
  allowedFontSizes,
  fontKey2NameMap,
  getDefaultValByKeyOrType,
} from "../../../../../../helpers";

type ComponentResolver = ({
  Key,
  Name,
  Type,
  Value,
}: IApplicationConfigurationBrandingValueModel) => JSX.Element;

interface IValuesHandler {
  [key: string]: ComponentResolver;
}

const createValuesHandler = (
  t: UseTranslationResponse["t"]
): IValuesHandler => {
  return {
    BOOL: ({
      Key,
      Name,
      Type,
      Value: propsValue = getDefaultValByKeyOrType({ Key, Type }),
    }: IApplicationConfigurationBrandingValueModel) => {
      const Value = propsValue === "true";

      return (
        <Form.Item
          key={Key}
          name={Key}
          initialValue={Value}
          valuePropName="checked"
        >
          <Checkbox />
        </Form.Item>
      );
    },
    COLOR: ({
      Key,
      Name,
      Type,
      Value = getDefaultValByKeyOrType({ Key, Type }),
    }: IApplicationConfigurationBrandingValueModel) => {
      return (
        <Form.Item
          key={Key}
          name={Key}
          initialValue={Value}
          valuePropName="hex"
        >
          <ColorPicker
            color={Value as string}
            pickerStyle={{ right: "-55px" }}
          />
        </Form.Item>
      );
    },
    FONT: ({
      Key,
      Name,
      Type,
      Value = getDefaultValByKeyOrType({ Key, Type }),
    }: IApplicationConfigurationBrandingValueModel) => (
      <Form.Item key={Key} name={Key} initialValue={Value}>
        <Choose
          allowClear={false}
          placeholder={t("Choose font", "Choose font")}
        >
          {allowedFonts.map((fontName) => (
            <ChooseOption key={fontName} value={fontName}>
              {fontKey2NameMap[fontName]}
            </ChooseOption>
          ))}
        </Choose>
      </Form.Item>
    ),
    GENERIC_TEXT: ({
      Key,
      Name,
      Type,
      Value = getDefaultValByKeyOrType({ Key, Type }),
    }: IApplicationConfigurationBrandingValueModel) => {
      return (
        <Form.Item key={Key} name={Key} initialValue={Value}>
          <Input placeholder={t(`Enter value`)} />
        </Form.Item>
      );
    },
    NUMBER: ({
      Key,
      Name,
      Type,
      Value = getDefaultValByKeyOrType({ Key, Type }),
    }: IApplicationConfigurationBrandingValueModel) => {
      return (
        <Form.Item key={Key} name={Key} initialValue={Value}>
          <Choose
            allowClear={false}
            placeholder={t("Choose font size", "Choose font size")}
          >
            {allowedFontSizes.map((fontSize) => (
              <ChooseOption key={fontSize} value={fontSize}>
                {fontSize}
              </ChooseOption>
            ))}
          </Choose>
        </Form.Item>
      );
    },
    URL: ({
      Key,
      Name,
      Type,
      Url = getDefaultValByKeyOrType({ Key, Type }),
    }: IApplicationConfigurationBrandingValueModel) => (
      <img src={`${Url}`} alt="" className="imagePreview" />
    ),
  };
};

interface IGetColumnsProps {
  t: UseTranslationResponse["t"];
  valuesHandler: IValuesHandler;
  setImageModalData?: (
    imageData: IApplicationConfigurationBrandingValueModel
  ) => any;
}

const getColumnsProps = ({
  t,
  valuesHandler,
  setImageModalData,
}: IGetColumnsProps): Array<
  ITableColumnProps<IApplicationConfigurationBrandingValueModel>
> => [
  {
    key: "Key",
    dataIndex: "Key",
    title: t("Key", "Key"),
    render: (text, row) => {
      const { Type } = row;
      if (Type !== "URL" || typeof setImageModalData !== "function") {
        return <span>{text}</span>;
      }

      const imageData = {
        ...row,
      };

      const handleImageNameClick = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        setImageModalData(imageData);
      };
      return <a onClick={handleImageNameClick}>{text}</a>;
    },
  },
  {
    key: "Name",
    dataIndex: "Name",
    title: t("name", "Name"),
  },
  {
    key: "Value",
    dataIndex: "Value",
    title: t("value", "Value"),
    render: (text, { Value, Key, Name, Type, Url }) => {
      return valuesHandler[Type]({
        Key,
        Name,
        Type,
        Value,
        Url,
      });
    },
  },
];

interface IApplicationConfigurationBrandingValueModelsGroup {
  label: string;
  dataSource: IApplicationConfigurationBrandingValueModel[];
  setImageModalData?: (
    imageData: IApplicationConfigurationBrandingValueModel
  ) => any;
}

const getRowKey = ({ Key }: IApplicationConfigurationBrandingKeyTypeModel) =>
  `${Key}`;

const FieldsGroupDef: React.FC<IApplicationConfigurationBrandingValueModelsGroup> = (
  props
) => {
  const { label, dataSource, setImageModalData } = props;

  const [columns, setColumns] = useState<
    Array<ITableColumnProps<IApplicationConfigurationBrandingValueModel>>
  >([]);

  const { t } = useTranslation();

  useEffect(() => {
    const valuesHandler = createValuesHandler(t);

    setColumns(getColumnsProps({ t, valuesHandler, setImageModalData }));
  }, []);

  return !!columns.length ? (
    <>
      <h3 className="branding-value-group-title">{label}</h3>
      {!!columns.length && (
        <Table<IApplicationConfigurationBrandingValueModel>
          className="branding-value-group-table"
          columns={columns}
          dataSource={dataSource}
          loading={false}
          rowKey={getRowKey}
          pagination={false}
        />
      )}
    </>
  ) : null;
};

export const FieldsGroup = React.memo(FieldsGroupDef);
